import clsx from 'clsx'
import FlagControlArea from 'components/flags/FlagControlArea'
import { Portal } from 'components/utility/portal'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { selectFlagsForTarget, useFlagStore } from 'stores/flagStore'
import { selectDocumentOfRequirement, selectRequirementTitle, usePackageStore } from 'stores/packageStore'
import { useShallow } from 'zustand/react/shallow'

const DocumentPreviewWithFlagViewer = ({
  role,
  pdfUrl,
  isSidebarCollapsed,
  title,
  subtitle,
  requirementId,
  flaggableId,
  flaggableType,
  packageHashedId,
  processorWorking,
  currentRoundNumber,
  handleClose,
}) => {
  const [collapse, setCollapse] = useState(false)
  const flags = useFlagStore(useShallow(selectFlagsForTarget(flaggableType, flaggableId)))
  const requirementTitle = usePackageStore(selectRequirementTitle(requirementId))
  const documentData = usePackageStore(selectDocumentOfRequirement(requirementId, flaggableId))

  useLayoutEffect(() => {
    setCollapse(isSidebarCollapsed)
  }, [isSidebarCollapsed])

  return (
    <div className="dome-full-width dome-full-height dome-d-flex">
      <object data={pdfUrl} type="application/pdf" className="doc-preview-modal__pdf">
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
          Open PDF
        </a>
      </object>
      <div className="dome-relative">
        <div
          className={clsx(
            'doc-preview-modal__flag-area-body',
            collapse && 'doc-preview-modal__flag-area-body__collapse'
          )}
        >
          <FlagControlArea
            target={{
              flaggableType: flaggableType,
              flaggableId: flaggableId,
              packageId: packageHashedId,
            }}
            role={role}
            title={title ?? documentData?.label}
            subtitle={subtitle ?? requirementTitle}
            processorWorking={processorWorking}
            currentRoundNumber={currentRoundNumber}
            onClose={handleClose}
            className="doc-preview-modal__flag-area"
          />
        </div>
        {!collapse && (
          <a target="_blank" href={pdfUrl} rel="noopener noreferrer" className="doc-preview-modal__new-tab-icon">
            <i className="fa-solid fa-sm fa-arrow-up-right-and-arrow-down-left-from-center dome-color-aqua"></i>
          </a>
        )}
        <button type="button" onClick={() => setCollapse(!collapse)} className="doc-preview-modal__flag-icon">
          <div className="dome-full-height dome-d-flex dome-align-center dome-gap9">
            <i className={clsx('fa-light dome-color-white', collapse ? 'fa-angle-left' : 'fa-angle-right')}></i>
            <div className="dome-d-flex dome-align-center dome-gap6">
              {collapse && <span className="dome-h5 dome-color-white">{flags.length}</span>}
              <i className="fa-solid fa-flag-swallowtail fa-xs dome-color-white" />
            </div>
          </div>
        </button>
      </div>
    </div>
  )
}

export function DocumentPreviewModal(props) {
  const { isOpen } = props

  const [eventOpen, setEventOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [documentId, setDocumentId] = useState(null)
  const [requirementId, setRequirementId] = useState(null)
  const [flaggableType, setFlaggableType] = useState(null)
  const [openFlagViewer, setOpenFlagViewer] = useState(false)

  const open = isOpen || eventOpen

  const handleClose = useCallback(() => {
    setEventOpen(false)
    setPdfUrl(null)
    setRequirementId(null)
    setDocumentId(null)
    setFlaggableType(null)
    setOpenFlagViewer(false)
    document.dispatchEvent(new CustomEvent('package:flag-modal-closed'))
  }, [])

  useEffect(() => {
    if (!open) return
    document.body.classList.toggle('overlay-activated', open)
    return () => {
      document.body.classList.remove('overlay-activated')
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, handleClose])

  useEffect(() => {
    const handler = (e) => {
      const { requirementId, documentId, pdfUrl, withFlagsDrawer, flaggableType } = e.detail
      setEventOpen(true)
      setPdfUrl(pdfUrl)
      setDocumentId(documentId)
      setRequirementId(requirementId)
      setFlaggableType(flaggableType)
      setOpenFlagViewer(!withFlagsDrawer)
    }
    document.addEventListener('package:preview-open', handler)
    return () => document.removeEventListener('package:preview-open', handler)
  }, [])

  if (!open || !documentId) return null

  return (
    <Portal>
      <div className="doc-preview-backdrop" onClick={handleClose}>
        <div role="dialog" aria-modal="true" className="doc-preview-modal__body" onClick={(e) => e.stopPropagation()}>
          <DocumentPreviewWithFlagViewer
            {...props}
            pdfUrl={pdfUrl}
            requirementId={requirementId}
            flaggableId={documentId}
            isSidebarCollapsed={openFlagViewer}
            handleClose={handleClose}
            flaggableType={flaggableType ?? props.flaggableType}
          />
        </div>
      </div>
    </Portal>
  )
}

export default DocumentPreviewModal
